import { setAppName } from '@/composables/usePageTitle.js';
import { invalidateBranchStockCaches } from '@/indexedDB/offlineDB.js';
import { clearSearchCoverage } from '@/services/posSearchCoverage.js';
import apiClient from '@/utils/axios.js';
import {
    isDefaultCompanyLogo as isDefaultCompanyLogoUrl,
    lineDetailSwitches,
    POS_LINE_DETAIL_DEFAULTS,
    POS_LINE_DETAIL_PREFIX,
    SALE_RECEIPT_LINE_DETAIL_PREFIX,
} from '@/utils/receiptDesign.js';
import { defineStore } from 'pinia';

export const DEFAULT_COMPANY_LOGO = '/uploads/company/default-logo.png';
const DEFAULT_FAVICON = '/favicon.ico';

// One test for "is this the placeholder", shared with the receipts, which
// refuse to print it. Here a missing row counts as the placeholder too: the
// app has to draw something, and the placeholder is what it draws.
const isDefaultCompanyLogo = (logo) => !logo || isDefaultCompanyLogoUrl(logo);

/**
 * Read a configuration row as a boolean, using the same truthy vocabulary the
 * server does. `fallback` is what a missing row means: for the optional header
 * taxes that is `true`, matching App\Services\TaxComponentSettings — an
 * installation that has never opened the settings screen must keep charging
 * exactly what it charges today.
 */
const configFlagOn = (value, fallback = false) => {
    if (value === undefined || value === null || value === '') return fallback;
    return ['1', 'true', 'yes', 'on'].includes(String(value).trim().toLowerCase());
};

/**
 * Which price a POS line starts from: `fixed`, `latest_receiving` or `batch`.
 * A missing or unrecognised row reads as `latest_receiving`, which is what the
 * till has always charged and what the server resolves the same row to.
 */
export const posPricingModeOf = (configurations) => {
    const mode = String(configurations?.pos_pricing_mode ?? '').trim();
    return ['fixed', 'latest_receiving', 'batch'].includes(mode) ? mode : 'latest_receiving';
};

/**
 * Drop what the POS cached under the previous pricing mode.
 *
 * A switch of the mode reprices the whole catalogue without moving any stock:
 * every cached branch price, and in batch mode every cached batch list, was
 * resolved by the old one — and the cart charges the cached figure, because
 * the line's rate travels to the server as its `amount`. The server does tell
 * the tills, through a `branch_stocks` invalidate, but that is a broadcast: it
 * needs the queue worker and the socket, and the browser that has just saved
 * the setting hears it no sooner than any other. Inside branch stock's 30
 * second freshness window the POS therefore opened on the old mode's prices.
 *
 * So the store that watches the mode move drops both caches itself: the
 * branch-stock freshness markers, so the next POS read cannot skip its
 * refetch, and the search coverage memo, so the next keyword goes back to the
 * server for its batches. Markers and memo only — no cached row, and therefore
 * no cart reservation, is discarded. A cold store (nothing loaded before) has
 * no previous mode to have changed from.
 */
const dropPosPriceCachesOnModeChange = (before, after) => {
    if (!before || Object.keys(before).length === 0) return;
    if (posPricingModeOf(before) === posPricingModeOf(after)) return;

    clearSearchCoverage();
    void invalidateBranchStockCaches();
};

/**
 * A precision setting as the calculators read it: 4 when the row says so, 2
 * otherwise — a missing row is what every installation computed at before the
 * setting existed. Mirrors MoneyMath::normalizeDecimals.
 */
const decimalsSetting = (value) => (String(value ?? '').trim() === '4' ? 4 : 2);

const applyDocumentFavicon = (href) => {
    if (typeof document === 'undefined') return;

    const links = document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']");
    if (links.length) {
        links.forEach((link) => {
            link.setAttribute('href', href);
        });
        return;
    }

    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = href;
    document.head.appendChild(link);
};

export const useConfigurationStore = defineStore('configuration', {
    state: () => ({
        configurations: {},
        bankAccounts: [],
        addonFeatures: [],
        enabledAddonKeys: [],
        loaded: false,
        addonsLoaded: false,
        /** In-flight `/configurations/index` promise — see fetchConfigurations. */
        configurationsRequest: null,
        /**
         * The subscription's standing — `GET /billing/state`, also carried on
         * every 402 body. Cached in localStorage so a cold or offline boot can
         * still show the bar and refuse writes; the server is the authority
         * whenever it can be reached. Null until the first answer arrives.
         */
        billing: null,
        billingLoaded: false,
        /**
         * The currency registry (`GET /configurations/currencies`): every
         * currency with its current rate, the base flagged. Cached in
         * localStorage so a document form can still show codes offline; a
         * rate is only ever a default the server re-validates.
         */
        currencies: [],
        currenciesLoaded: false,
        currenciesRequest: null,
    }),
    getters: {
        companyLogo: (state) => {
            const logo = state.configurations.company_logo;
            return logo && !isDefaultCompanyLogo(logo) ? logo : DEFAULT_COMPANY_LOGO;
        },
        companyLogoWithVersion: (state) => {
            const logo = state.configurations.company_logo;
            const resolved = logo || DEFAULT_COMPANY_LOGO;
            return resolved + (resolved.includes('?') ? '&' : '?') + 't=' + Date.now();
        },
        hasCustomCompanyLogo: (state) => {
            return !isDefaultCompanyLogo(state.configurations.company_logo);
        },
        company_name: (state) => state.configurations.company_name || 'Inventree',
        // FBR advance income tax. Only ever used to decide whether to *show* the
        // advance-tax row — the rate that is charged is always the one the
        // server resolved for the buyer, never one derived from these.
        advanceTaxEnabled: (state) => configFlagOn(state.configurations?.advance_tax_enabled),
        // The optional header taxes. Off means the box is hidden and the rate is
        // forced to zero — the server refuses a disabled component outright, so
        // the screens must never quote one.
        furtherTaxEnabled: (state) => configFlagOn(state.configurations?.further_tax_enabled, true),
        fedTaxEnabled: (state) => configFlagOn(state.configurations?.fed_tax_enabled, true),
        extraTaxEnabled: (state) => configFlagOn(state.configurations?.extra_tax_enabled, true),
        advanceTaxRates: (state) => ({
            '236G': {
                filer: parseFloat(state.configurations?.advance_tax_236g_filer || 0),
                non_filer: parseFloat(state.configurations?.advance_tax_236g_non_filer || 0),
            },
            '236H': {
                filer: parseFloat(state.configurations?.advance_tax_236h_filer || 0),
                non_filer: parseFloat(state.configurations?.advance_tax_236h_non_filer || 0),
            },
        }),
        // The buy side of the same sections: whether suppliers collect
        // s.236G/236H from us. Separate from advanceTaxEnabled, which is about
        // what we collect from buyers — an installation may do either alone.
        purchaseAdvanceTaxEnabled: (state) => configFlagOn(state.configurations?.purchase_advance_tax_enabled),
        // How this installation's supplier invoices are usually written, and so
        // what the purchase screen opens on. Both default OFF, which is the
        // FBR-compliant reading a s.23 tax invoice prints: a line price
        // excluding sales tax, and a discount inside the value of supply. The
        // document itself records what it was actually entered on, so these
        // only ever seed a new order.
        purchasePricesIncludeTax: (state) => configFlagOn(state.configurations?.purchase_prices_include_tax),
        purchaseDiscountAfterTax: (state) => configFlagOn(state.configurations?.purchase_discount_after_tax),
        // Whether the retail price printed on a Third Schedule line carries the
        // sales tax. Default ON — the all-in figure on the pack, and the reading
        // every order before the setting was computed on — so a missing row
        // reads that way rather than as the s.2(27) exclusive figure.
        purchaseMrpIncludesTax: (state) => configFlagOn(state.configurations?.purchase_mrp_includes_tax, true),
        /**
         * What a purchase should pre-fill its advance tax with: the section we
         * are billed under and the rate our own ATL standing attracts.
         *
         * A default the operator can overwrite, because the figure that counts
         * is the one printed on the supplier's invoice. The server resolves the
         * same pair and refuses the charge outright when the setting is off.
         */
        purchaseAdvanceTaxDefaults(state) {
            const section = state.configurations?.purchase_advance_tax_section === '236G' ? '236G' : '236H';
            const isFiler = configFlagOn(state.configurations?.purchase_advance_tax_filer, true);
            return { section, isFiler, rate: this.advanceTaxRates[section][isFiler ? 'filer' : 'non_filer'] };
        },
        currency: (state) => state.configurations?.currency || 'PKR',
        // Multi-currency (docs/multi-currency-plan.md). The base is the
        // `currency` setting; a document or a tender in another currency
        // needs the addon on.
        multiCurrencyEnabled: (state) => state.enabledAddonKeys.includes('multi_currency'),
        baseCurrency: (state) => String(state.configurations?.currency || 'PKR').toUpperCase(),
        activeCurrencies: (state) => state.currencies.filter((currency) => currency.is_active),
        /** Today's rate for a code: '1' for the base, null for one the registry does not offer. */
        currencyRate: (state) => (code) => {
            const base = String(state.configurations?.currency || 'PKR').toUpperCase();
            const wanted = String(code || '').toUpperCase();
            if (!wanted || wanted === base) return '1';
            const row = state.currencies.find((currency) => currency.code === wanted && currency.is_active);
            return row ? String(row.exchange_rate) : null;
        },
        currencySymbol: (state) => (code) => {
            const wanted = String(code || state.configurations?.currency || 'PKR').toUpperCase();
            return state.currencies.find((currency) => currency.code === wanted)?.symbol || wanted;
        },
        // Whether the floating Ask bubble follows you around. Defaults ON,
        // because the addon is already the gate that decides whether anyone has
        // Ask at all — an operator who has licensed it almost certainly wants
        // it reachable, and this switch is for the ones who find it in the way.
        askLauncherEnabled: (state) => configFlagOn(state.configurations?.ask_launcher_enabled, true),
        // What the eye beside a cost figure starts as, on every screen that has
        // one. Defaults OFF — costs masked — which is what every screen has always
        // done, so an upgrade never starts revealing margins on its own. It is
        // the starting state only: the eye still toggles it for the session, and
        // `view_cost_access` still decides whether there is anything to reveal.
        costRevealDefaultVisible: (state) => configFlagOn(state.configurations?.cost_reveal_default_visible),
        // Whether the POS shows each cart line's rate as a box the cashier may
        // type over. Off by default: an upgrade must never start letting every
        // till reprice the shelf. Display-only — the server prices nothing
        // from it, and the typed rate travels as the line's `amount` exactly as
        // the catalogue rate always has.
        posRateOverrideEnabled: (state) => configFlagOn(state.configurations?.pos_rate_override_enabled),
        // Multi sale at the POS: the vendor's addon and the shop's own switch
        // (Configurations -> Sales & POS) both on. Then the till is a front
        // office / cash counter for everyone with a multi-sale permission, and
        // there is no button to leave it. Off by default.
        multiSaleEnabled: (state) => state.enabledAddonKeys.includes('multi_sale') && configFlagOn(state.configurations?.multi_sale_enabled),
        // Which price a POS line starts from: `fixed`, `latest_receiving` or
        // `batch`. A missing or unrecognised row reads as `latest_receiving`,
        // which is what the till has always charged. Separate from the rate
        // override above, which only decides whether the cashier may type over
        // the result.
        posPricingMode: (state) => posPricingModeOf(state.configurations),
        // Batch mode: receiving lines carry batch numbers and the POS lists one
        // search row per batch. Off by default, and while off no screen shows a
        // batch column, input or label anywhere.
        batchEnabled: (state) => state.configurations?.pos_pricing_mode === 'batch',
        // The places sales and purchases are computed and stored at, and the
        // places they are shown at — the Localization tab. Two unless the
        // installation chose four, and the display never shows more places than
        // the figures carry (the server clamps the pair as well). A document
        // freezes its own precision; these decide new ones and every screen.
        // Whether a new sale's payable settles to the whole rupee (Sales & POS).
        saleRoundTotal: (state) => configFlagOn(state.configurations?.sale_round_total),
        saleDecimalPlaces: (state) => decimalsSetting(state.configurations?.sale_decimal_places),
        saleDisplayDecimals: (state) =>
            Math.min(decimalsSetting(state.configurations?.sale_decimal_places), decimalsSetting(state.configurations?.sale_display_decimal_places)),
        purchaseDecimalPlaces: (state) => decimalsSetting(state.configurations?.purchase_decimal_places),
        purchaseDisplayDecimals: (state) =>
            Math.min(
                decimalsSetting(state.configurations?.purchase_decimal_places),
                decimalsSetting(state.configurations?.purchase_display_decimal_places),
            ),
        // Whether the POS shell folds the sidebar to its rail regardless of the
        // width the user chose. Defaults ON — what the POS has always done —
        // so a missing row does not widen every till.
        posSidebarAutoCollapse: (state) => configFlagOn(state.configurations?.pos_sidebar_auto_collapse, true),
        isAddonEnabled: (state) => (key) => state.enabledAddonKeys.includes(key),
        billingState: (state) => state.billing,
        /**
         * Whether the lock is on. `lock_mode: 'none'` is a vendor who wants the
         * bar and nothing else, so it never locks whatever the server's flag
         * says — the same double check BillingGate makes on its side.
         */
        billingLocked: (state) => Boolean(state.billing?.locked) && state.billing?.lock_mode !== 'none',
        billingLockMode: (state) => state.billing?.lock_mode ?? 'read_only',
        billingCanManage: (state) => Boolean(state.billing?.can_manage),
        receiptHideTax: (state) => configFlagOn(state.configurations?.receipt_hide_tax),
        receiptHideDiscount: (state) => configFlagOn(state.configurations?.receipt_hide_discount),
        // The two detail lines beside a receipt's number and date: who served
        // it and how it was paid. Both print unless switched off, as they
        // always have.
        receiptHideCashier: (state) => configFlagOn(state.configurations?.receipt_hide_cashier),
        receiptHidePaymentMethod: (state) => configFlagOn(state.configurations?.receipt_hide_payment_method),
        // Whether the sale receipt prints the customer's account position
        // (outstanding across invoices, or store credit) under its totals.
        // Defaults ON to match the server row.
        saleReceiptCustomerBalanceEnabled: (state) => configFlagOn(state.configurations?.sale_receipt_customer_balance_enabled, true),
        // Sale invoice only: each line's Rate as the tax-inclusive unit price
        // (the Tax column folds into it), and the summary cut to the final
        // total. Both off unless switched on.
        saleReceiptTaxInclusiveRate: (state) => configFlagOn(state.configurations?.sale_receipt_tax_inclusive_rate),
        saleReceiptTotalsOnly: (state) => configFlagOn(state.configurations?.sale_receipt_totals_only),
        // The muted line under each product's name — family, SKU, material,
        // colour, size, type, dimensions — on the sale invoice (Receipts tab,
        // all off) and on the POS cart (Sales & POS tab, the family on). One
        // object of switches each, read by every builder and the cart through
        // lineDetailText().
        saleReceiptLineDetails: (state) => lineDetailSwitches(state.configurations ?? {}, SALE_RECEIPT_LINE_DETAIL_PREFIX),
        posLineDetails: (state) => lineDetailSwitches(state.configurations ?? {}, POS_LINE_DETAIL_PREFIX, POS_LINE_DETAIL_DEFAULTS),
    },

    actions: {
        applyFavicon() {
            const logo = this.configurations.company_logo;
            if (logo && !isDefaultCompanyLogo(logo)) {
                applyDocumentFavicon(logo + (logo.includes('?') ? '&' : '?') + 't=' + Date.now());
                return;
            }
            applyDocumentFavicon(DEFAULT_FAVICON);
        },
        loadFromCache() {
            const cached = localStorage.getItem('configurations');
            if (cached) {
                this.configurations = JSON.parse(cached);
                this.applyFavicon();
                setAppName(this.company_name);
            }
            const cachedAddons = localStorage.getItem('addonFeatures');
            if (cachedAddons) {
                try {
                    const parsed = JSON.parse(cachedAddons);
                    this.addonFeatures = parsed.features || [];
                    this.enabledAddonKeys = parsed.enabled || [];
                } catch (e) {
                    // ignore corrupt cache
                }
            }
            const cachedBilling = localStorage.getItem('billingState');
            if (cachedBilling && this.billing === null) {
                try {
                    const parsed = JSON.parse(cachedBilling);
                    if (parsed && typeof parsed === 'object') {
                        this.billing = parsed;
                    }
                } catch (e) {
                    // ignore corrupt cache
                }
            }
        },
        setBillingState(state) {
            if (!state || typeof state !== 'object') return;
            this.billing = state;
            this.billingLoaded = true;
            try {
                localStorage.setItem('billingState', JSON.stringify(state));
            } catch (e) {
                // Storage full or blocked: the in-memory copy still drives the bar.
            }
        },
        async fetchBillingState() {
            try {
                const response = await apiClient.get('/billing/state', { skipProgress: true });
                this.setBillingState(response.data?.data ?? null);
            } catch (error) {
                console.error('Failed to fetch billing state', error);
            }
        },
        setBankAccounts(accounts) {
            this.bankAccounts = accounts;
        },

        /** The currency registry, shared while in flight and cached for offline reads. */
        async fetchCurrencies(force = false) {
            if (this.currenciesRequest) return this.currenciesRequest;
            if (this.currenciesLoaded && !force) return this.currencies;

            this.currenciesRequest = apiClient
                .get('/configurations/currencies', { skipProgress: true })
                .then((response) => {
                    this.currencies = response.data?.data || [];
                    this.currenciesLoaded = true;
                    try {
                        localStorage.setItem('currencies', JSON.stringify(this.currencies));
                    } catch {
                        /* storage unavailable — the network copy still stands */
                    }
                    return this.currencies;
                })
                .catch((error) => {
                    console.error('Failed to fetch currencies', error);
                    if (!this.currenciesLoaded) {
                        try {
                            this.currencies = JSON.parse(localStorage.getItem('currencies') || '[]');
                        } catch {
                            this.currencies = [];
                        }
                    }
                    return this.currencies;
                })
                .finally(() => {
                    this.currenciesRequest = null;
                });

            return this.currenciesRequest;
        },

        /**
         * Shared while in flight, because two callers race on every boot.
         *
         * App.vue's login watcher fires this during parent setup, and the
         * sidebar's `onMounted` fires it again a moment later — its guard is
         * `if (!configStore.loaded)`, and `loaded` is only set on the *response*,
         * which has not arrived yet. So every page load, on both shells, ran two
         * identical `/configurations/index` queries. Deduping here rather than by
         * having `loadFromCache` set `loaded` keeps the cache and the network
         * answer distinct: a cached copy is worth rendering, not worth trusting
         * as current.
         */
        async fetchConfigurations() {
            if (this.configurationsRequest) return this.configurationsRequest;

            this.configurationsRequest = this.requestConfigurations().finally(() => {
                this.configurationsRequest = null;
            });

            return this.configurationsRequest;
        },

        async requestConfigurations() {
            try {
                const response = await apiClient.get('/configurations/index');
                const newConfigs = {};
                for (const config of response.data) {
                    newConfigs[config.name] = config.value;
                }
                dropPosPriceCachesOnModeChange(this.configurations, newConfigs);
                this.configurations = newConfigs;
                localStorage.setItem('configurations', JSON.stringify(newConfigs));
                this.loaded = true;
                this.applyFavicon();
                setAppName(this.company_name);
            } catch (error) {
                console.error('Failed to fetch configurations', error);
            }
        },

        async fetchAddonFeatures() {
            try {
                const response = await apiClient.get('/addon-features/index');
                this.addonFeatures = response.data?.data || [];
                this.enabledAddonKeys = response.data?.enabled || [];
                localStorage.setItem(
                    'addonFeatures',
                    JSON.stringify({
                        features: this.addonFeatures,
                        enabled: this.enabledAddonKeys,
                    }),
                );
                this.addonsLoaded = true;
            } catch (error) {
                console.error('Failed to fetch addon features', error);
            }
        },

        setConfigurations(configs) {
            dropPosPriceCachesOnModeChange(this.configurations, configs);
            // A copy, never the caller's object. The settings screen hands over
            // the very object its form is bound to, and holding that one meant
            // every unsaved edit made afterwards — a pricing mode picked and
            // then cancelled — was already live here, and the next save had no
            // previous value left to compare against.
            this.configurations = { ...configs };
            localStorage.setItem('configurations', JSON.stringify(configs));
            this.applyFavicon();
            setAppName(this.company_name);
        },
    },
});
